import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal, ImageReveal, LineReveal } from "../components/motion/Reveal";
import { GALLERY } from "../lib/site";
import { X } from "lucide-react";

// Asymmetric span pattern (repeats)
const SPANS = [
  "col-span-6 md:col-span-8 aspect-[16/10]",
  "col-span-6 md:col-span-4 aspect-[4/5]",
  "col-span-3 md:col-span-4 aspect-square",
  "col-span-3 md:col-span-4 aspect-square",
  "col-span-6 md:col-span-4 aspect-[4/5]",
  "col-span-6 md:col-span-12 aspect-[21/9]",
  "col-span-6 md:col-span-5 aspect-[4/5]",
  "col-span-6 md:col-span-7 aspect-[16/10]",
];

export default function Gallery() {
  const [active, setActive] = useState(null);

  return (
    <div className="bg-bg">
      <section className="pt-40 pb-16 md:pt-52 md:pb-20" data-testid="gallery-hero">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-9">
            <div className="overline text-terracotta mb-6">Gallery</div>
            <LineReveal
              className="font-display leading-[0.9] tracking-[-0.02em]"
              lineClassName="block text-6xl md:text-7xl lg:text-[8rem] text-ink font-bold"
              lines={["Interior. Terrace.", <span key="i" className="italic text-terracotta">Rooms.</span>]}
            />
          </div>
          <div className="md:col-span-3">
            <Reveal delay={0.4}>
              <p className="text-ink-soft leading-relaxed text-sm">
                A slow walk through painted courtyards, ceiling frescoes and the rooftop view that made
                Kipling stay in Bundi. Click any image to enlarge.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="pb-24 md:pb-32" data-testid="gallery-grid">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <div className="grid grid-cols-6 md:grid-cols-12 gap-3 md:gap-4">
            {GALLERY.map((src, i) => (
              <Reveal key={i} delay={(i % 6) * 0.04} className={SPANS[i % SPANS.length]}>
                <button
                  onClick={() => setActive(src)}
                  className="group w-full h-full overflow-hidden border border-[#E5E1D8] block relative"
                  data-testid={`gallery-tile-${i}`}
                >
                  <ImageReveal src={src} alt={`Haveli ${i+1}`} className="w-full h-full" imgClassName="group-hover:scale-105 transition-transform duration-1000"/>
                  <div className="absolute inset-0 bg-navy/0 group-hover:bg-navy/15 transition-colors duration-500"/>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-navy/95 flex items-center justify-center p-6 md:p-12"
            onClick={() => setActive(null)}
            data-testid="lightbox"
          >
            <button
              className="absolute top-6 right-6 w-11 h-11 rounded-full border border-bg/30 text-bg flex items-center justify-center hover:bg-bg hover:text-navy transition-colors"
              onClick={() => setActive(null)}
              aria-label="Close"
              data-testid="lightbox-close"
            >
              <X size={18}/>
            </button>
            <motion.img
              key={active}
              src={active}
              alt="Enlarged"
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.6, ease: [0.2, 0.7, 0.2, 1] }}
              className="max-h-[85vh] max-w-full object-contain border border-bg/10"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
